import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import BranchSelector from './BranchSelector';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import jsPDF from 'jspdf';
import './SchoolDashboard.css';

function InvoicePreview() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId, handleBranchChange } = useBranchFilter();
  
  // Selectors
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('');
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [viewMode, setViewMode] = useState('all'); // 'all', 'class-wise'
  
  // Data
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [schoolLogo, setSchoolLogo] = useState(null);

  // Helper function to get base class name without section
  const getClassDisplayName = (className) => {
    if (!className) return 'No Class';
    if (className.includes(' - ')) {
      return className.split(' - ')[0].trim();
    }
    return className.trim();
  };

  // Helper function to format numbers with commas
  const formatNumber = (num) => {
    return parseFloat(num || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  useEffect(() => {
    const storedSchoolData = localStorage.getItem('schoolData');
    if (!storedSchoolData) {
      navigate('/schoollogin');
      return;
    }
    setSchoolData(JSON.parse(storedSchoolData));
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (schoolData?.id) {
      fetchAcademicYears();
      fetchClasses();
      fetchSchoolLogo();
    }
  }, [schoolData?.id, selectedBranchId]);

  useEffect(() => {
    if (schoolData?.id && selectedAcademicYear) {
      fetchInvoices();
    }
  }, [schoolData?.id, selectedBranchId, selectedAcademicYear, selectedClass, viewMode, academicYears]);

  const fetchAcademicYears = async () => {
    try {
      const { data, error } = await supabase
        .from('academic_years')
        .select('*')
        .eq('school_id', schoolData.id)
        .order('start_date', { ascending: false });

      if (error) {
        console.error('Error fetching academic years:', error);
        return;
      }

      setAcademicYears(data || []);
      if (data && data.length > 0 && !selectedAcademicYear) {
        setSelectedAcademicYear(data[0].id);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchClasses = async () => {
    try {
      let query = supabase
        .from('classes')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('is_active', true)
        .order('class_name', { ascending: true });

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      } else {
        query = query.is('branch_id', null);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching classes:', error);
        return;
      }

      setClasses(data || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchSchoolLogo = async () => {
    if (!schoolData?.logo_url) return;
    
    try {
      const { data, error } = await supabase.storage
        .from('school-logos')
        .download(schoolData.logo_url);
      
      if (error) throw error;
      
      const url = URL.createObjectURL(data);
      setSchoolLogo(url);
    } catch (error) {
      console.error('Error fetching school logo:', error);
    }
  };

  const fetchInvoices = async () => {
    try {
      setLoadingInvoices(true);
      
      let query = supabase
        .from('student_invoices')
        .select(`
          *,
          students:student_id (
            id,
            first_name,
            middle_name,
            last_name,
            student_id_no,
            class_id,
            admission_date,
            classes:class_id (id, class_name)
          )
        `)
        .eq('school_id', schoolData.id)
        .eq('academic_year_id', selectedAcademicYear)
        .order('generated_at', { ascending: false });

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      } else {
        query = query.is('branch_id', null);
      }

      if (viewMode === 'class-wise' && selectedClass) {
        // Filter by students in the selected class
        const { data: classStudents } = await supabase
          .from('students')
          .select('id')
          .eq('class_id', selectedClass)
          .eq('is_active', true);
        
        if (classStudents && classStudents.length > 0) {
          const studentIds = classStudents.map(s => s.id);
          query = query.in('student_id', studentIds);
        } else {
          setInvoices([]);
          setLoadingInvoices(false);
          return;
        }
      }

      const { data: invoicesData, error } = await query;

      if (error) {
        console.error('Error fetching invoices:', error);
        console.error('Query details:', {
          school_id: schoolData.id,
          academic_year_id: selectedAcademicYear,
          branch_id: selectedBranchId,
          viewMode,
          selectedClass
        });
        setInvoices([]);
        return;
      }

      console.log('Fetched invoices:', invoicesData);
      console.log('Number of invoices found:', invoicesData?.length || 0);

      // For each invoice, generate fee items from Class Fee Mapping (like Billing page does)
      if (invoicesData && invoicesData.length > 0) {
        // Get all unique class IDs from students
        const classIds = [...new Set(invoicesData
          .map(inv => inv.students?.class_id)
          .filter(Boolean))];
        
        // Fetch class fee mappings for these classes
        let mappingsQuery = supabase
          .from('class_fee_mapping')
          .select('*')
          .eq('school_id', schoolData.id)
          .eq('academic_year_id', selectedAcademicYear)
          .eq('status', 'published')
          .in('class_id', classIds);

        if (selectedBranchId) {
          mappingsQuery = mappingsQuery.or(`branch_id.eq.${selectedBranchId},branch_id.is.null`);
        } else {
          mappingsQuery = mappingsQuery.is('branch_id', null);
        }

        const { data: feeMappings, error: mappingsError } = await mappingsQuery;

        if (mappingsError) {
          console.error('Error fetching fee mappings:', mappingsError);
        } else {
          console.log('Fetched fee mappings for invoice preview:', feeMappings);
        }

        // Also try to fetch invoice_items as backup (if they exist)
        const invoiceIds = invoicesData.map(inv => inv.id);
        const { data: itemsData } = await supabase
          .from('invoice_items')
          .select('*')
          .in('invoice_id', invoiceIds);

        console.log('Fetched invoice items from DB:', itemsData);

        // Map items to invoices - use invoice_items if available, otherwise generate from Class Fee Mapping
        const invoicesWithItems = invoicesData.map(invoice => {
          const student = invoice.students;
          let items = [];

          // First, try to use invoice_items from database
          if (itemsData && itemsData.length > 0) {
            items = itemsData.filter(item => parseInt(item.invoice_id) === parseInt(invoice.id));
          }

          // If no invoice_items found, generate from Class Fee Mapping (like Billing page)
          if (items.length === 0 && student && student.class_id && feeMappings) {
            const classMappings = feeMappings.filter(mapping => 
              parseInt(mapping.class_id) === parseInt(student.class_id)
            );

            // Check if mapping applies to this student
            classMappings.forEach(mapping => {
              let appliesToStudent = false;
              
              if (mapping.applies_to === 'All') {
                appliesToStudent = true;
              } else if (mapping.applies_to === 'New') {
                // Check if student is new based on admission_date
                if (student.admission_date) {
                  const admissionDate = new Date(student.admission_date);
                  const academicYear = academicYears.find(ay => ay.id === parseInt(selectedAcademicYear));
                  if (academicYear && academicYear.start_date) {
                    const yearStart = new Date(academicYear.start_date);
                    appliesToStudent = admissionDate >= yearStart;
                  } else {
                    appliesToStudent = true;
                  }
                } else {
                  appliesToStudent = true;
                }
              } else if (mapping.applies_to === 'Returning') {
                // Check if student is returning
                if (student.admission_date) {
                  const admissionDate = new Date(student.admission_date);
                  const academicYear = academicYears.find(ay => ay.id === parseInt(selectedAcademicYear));
                  if (academicYear && academicYear.start_date) {
                    const yearStart = new Date(academicYear.start_date);
                    appliesToStudent = admissionDate < yearStart;
                  } else {
                    appliesToStudent = true;
                  }
                } else {
                  appliesToStudent = true;
                }
              }

              if (appliesToStudent) {
                items.push({
                  id: `temp-${mapping.id}`,
                  invoice_id: invoice.id,
                  fee_name: mapping.fee_name,
                  amount: parseFloat(mapping.amount || 0),
                  discount: 0, // Default discount
                  balance: parseFloat(mapping.amount || 0)
                });
              }
            });
          }

          console.log(`Invoice ${invoice.id} - Found ${items.length} items`);
          
          return {
            ...invoice,
            invoice_items: items
          };
        });

        console.log('Invoices with items:', invoicesWithItems);
        console.log('Total invoices:', invoicesWithItems.length);
        invoicesWithItems.forEach((inv, idx) => {
          console.log(`Invoice ${idx + 1}:`, {
            id: inv.id,
            invoice_number: inv.invoice_number,
            items_count: inv.invoice_items?.length || 0,
            items: inv.invoice_items
          });
        });
        
        setInvoices(invoicesWithItems);
      } else {
        console.log('No invoices found. Make sure invoices are generated from the Billing page first.');
        setInvoices([]);
      }
    } catch (error) {
      console.error('Error:', error);
      setInvoices([]);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const generateInvoicePDF = (invoice) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPos = margin;

    const student = invoice.students;
    const invoiceItems = invoice.invoice_items || [];

    // Helper function to add a new page if needed
    const checkPageBreak = (requiredSpace = 20) => {
      if (yPos + requiredSpace > doc.internal.pageSize.getHeight() - margin) {
        doc.addPage();
        yPos = margin;
        return true;
      }
      return false;
    };

    // School Logo (if available)
    if (schoolLogo) {
      try {
        const img = new Image();
        img.src = schoolLogo;
        doc.addImage(img, 'PNG', margin, yPos, 40, 20);
        yPos += 25;
      } catch (error) {
        console.error('Error adding logo to PDF:', error);
      }
    }

    // Header
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('INVOICE', pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;

    // School Information
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(schoolData.school_name || 'School Name', margin, yPos);
    yPos += 6;
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    if (schoolData.address) {
      doc.text(schoolData.address, margin, yPos);
      yPos += 5;
    }
    if (schoolData.phone) {
      doc.text(`Phone: ${schoolData.phone}`, margin, yPos);
      yPos += 5;
    }
    if (schoolData.email) {
      doc.text(`Email: ${schoolData.email}`, margin, yPos);
      yPos += 5;
    }
    yPos += 5;

    // Invoice Details
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(`Invoice Number: ${invoice.invoice_number}`, pageWidth - margin, margin + 5, { align: 'right' });
    doc.text(`Date: ${new Date(invoice.generated_at).toLocaleDateString()}`, pageWidth - margin, margin + 10, { align: 'right' });
    
    const academicYear = academicYears.find(ay => ay.id === invoice.academic_year_id);
    if (academicYear) {
      doc.text(`Academic Year: ${academicYear.year_name || 'N/A'}`, pageWidth - margin, margin + 15, { align: 'right' });
    }
    if (invoice.term) {
      doc.text(`Term: ${invoice.term}`, pageWidth - margin, margin + 20, { align: 'right' });
    }
    yPos = margin + 30;

    // Student Information
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Bill To:', margin, yPos);
    yPos += 8;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const studentName = `${student?.first_name || ''} ${student?.middle_name || ''} ${student?.last_name || ''}`.trim();
    doc.text(`Student Name: ${studentName}`, margin, yPos);
    yPos += 6;
    
    if (student?.student_id_no) {
      doc.text(`Admission No: ${student.student_id_no}`, margin, yPos);
      yPos += 6;
    }
    
    if (student?.classes?.class_name) {
      doc.text(`Class: ${getClassDisplayName(student.classes.class_name)}`, margin, yPos);
      yPos += 6;
    }
    yPos += 5;

    // Line
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;

    // Table Header
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Fee Name', margin, yPos);
    doc.text('Amount', pageWidth - margin - 60, yPos, { align: 'right' });
    doc.text('Discount', pageWidth - margin - 30, yPos, { align: 'right' });
    doc.text('Balance', pageWidth - margin, yPos, { align: 'right' });
    yPos += 5;

    // Line under header
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 6;

    // Table Rows
    doc.setFont('helvetica', 'normal');
    invoiceItems.forEach((item) => {
      checkPageBreak(15);
      
      doc.text(item.fee_name || 'N/A', margin, yPos);
      doc.text(`Rs ${formatNumber(item.amount)}`, pageWidth - margin - 60, yPos, { align: 'right' });
      doc.text(`Rs ${formatNumber(item.discount)}`, pageWidth - margin - 30, yPos, { align: 'right' });
      doc.text(`Rs ${formatNumber(item.balance)}`, pageWidth - margin, yPos, { align: 'right' });
      yPos += 8;
    });

    yPos += 5;
    checkPageBreak(20);

    // Totals
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Total Amount:', pageWidth - margin - 60, yPos, { align: 'right' });
    doc.text(`Rs ${formatNumber(invoice.total_amount)}`, pageWidth - margin, yPos, { align: 'right' });
    yPos += 8;

    doc.text('Total Discount:', pageWidth - margin - 60, yPos, { align: 'right' });
    doc.text(`Rs ${formatNumber(invoice.total_discount)}`, pageWidth - margin, yPos, { align: 'right' });
    yPos += 8;

    doc.setFontSize(12);
    doc.text('Total Due:', pageWidth - margin - 60, yPos, { align: 'right' });
    doc.text(`Rs ${formatNumber(invoice.total_balance)}`, pageWidth - margin, yPos, { align: 'right' });
    yPos += 10;

    // Footer
    checkPageBreak(15);
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;

    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.text('Thank you for your payment!', pageWidth / 2, yPos, { align: 'center' });
    yPos += 5;
    doc.text('This is a computer-generated invoice.', pageWidth / 2, yPos, { align: 'center' });

    // Save PDF
    const fileName = `Invoice_${invoice.invoice_number}_${studentName.replace(/\s+/g, '_')}.pdf`;
    doc.save(fileName);
  };

  const handlePrint = (invoice) => {
    // Create a printable version
    const printWindow = window.open('', '_blank');
    const student = invoice.students;
    const invoiceItems = invoice.invoice_items || [];
    const studentName = `${student?.first_name || ''} ${student?.middle_name || ''} ${student?.last_name || ''}`.trim();
    const academicYear = academicYears.find(ay => ay.id === invoice.academic_year_id);

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice - ${invoice.invoice_number}</title>
          <style>
            @media print {
              @page { margin: 20mm; }
            }
            body {
              font-family: Arial, sans-serif;
              max-width: 800px;
              margin: 0 auto;
              padding: 20px;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
            }
            .school-info {
              margin-bottom: 20px;
            }
            .invoice-details {
              text-align: right;
              margin-bottom: 20px;
            }
            .student-info {
              margin-bottom: 20px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 12px;
              text-align: left;
            }
            th {
              background-color: #f2f2f2;
              font-weight: bold;
            }
            .text-right {
              text-align: right;
            }
            .totals {
              margin-top: 20px;
              text-align: right;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              font-style: italic;
              color: #666;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>INVOICE</h1>
          </div>
          
          <div class="school-info">
            <h2>${schoolData.school_name || 'School Name'}</h2>
            ${schoolData.address ? `<p>${schoolData.address}</p>` : ''}
            ${schoolData.phone ? `<p>Phone: ${schoolData.phone}</p>` : ''}
            ${schoolData.email ? `<p>Email: ${schoolData.email}</p>` : ''}
          </div>
          
          <div class="invoice-details">
            <p><strong>Invoice Number:</strong> ${invoice.invoice_number}</p>
            <p><strong>Date:</strong> ${new Date(invoice.generated_at).toLocaleDateString()}</p>
            ${academicYear ? `<p><strong>Academic Year:</strong> ${academicYear.year_name || 'N/A'}</p>` : ''}
            ${invoice.term ? `<p><strong>Term:</strong> ${invoice.term}</p>` : ''}
          </div>
          
          <div class="student-info">
            <h3>Bill To:</h3>
            <p><strong>Student Name:</strong> ${studentName}</p>
            ${student?.student_id_no ? `<p><strong>Admission No:</strong> ${student.student_id_no}</p>` : ''}
            ${student?.classes?.class_name ? `<p><strong>Class:</strong> ${getClassDisplayName(student.classes.class_name)}</p>` : ''}
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Fee Name</th>
                <th class="text-right">Amount</th>
                <th class="text-right">Discount</th>
                <th class="text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              ${invoiceItems.map(item => `
                <tr>
                  <td>${item.fee_name || 'N/A'}</td>
                  <td class="text-right">Rs ${formatNumber(item.amount)}</td>
                  <td class="text-right">Rs ${formatNumber(item.discount)}</td>
                  <td class="text-right">Rs ${formatNumber(item.balance)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="totals">
            <p><strong>Total Amount:</strong> Rs ${formatNumber(invoice.total_amount)}</p>
            <p><strong>Total Discount:</strong> Rs ${formatNumber(invoice.total_discount)}</p>
            <p><strong>Total Due:</strong> Rs ${formatNumber(invoice.total_balance)}</p>
          </div>
          
          <div class="footer">
            <p>Thank you for your payment!</p>
            <p>This is a computer-generated invoice.</p>
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const handlePublishToParentApp = async (invoice) => {
    try {
      // Update invoice status to 'sent' (published to parent app)
      const { error } = await supabase
        .from('student_invoices')
        .update({ 
          status: 'sent',
          sent_at: new Date().toISOString()
        })
        .eq('id', invoice.id);

      if (error) throw error;

      alert('Invoice published to Parent APP successfully!');
      await fetchInvoices(); // Refresh the list
    } catch (error) {
      console.error('Error publishing invoice:', error);
      alert('Failed to publish invoice. Please try again.');
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
      />
      
      <SchoolSidebar schoolData={schoolData} />
      
      <div className="main-content" style={{ 
        width: '100%', 
        maxWidth: '100%', 
        overflowX: 'hidden',
        boxSizing: 'border-box',
        padding: '1.5rem'
      }}>
        <div className="content-header" style={{ 
          width: '100%', 
          maxWidth: '100%',
          boxSizing: 'border-box',
          marginBottom: '1rem'
        }}>
          <h1>Invoice Preview & Print</h1>
          <p>Preview and print invoices for students</p>
        </div>

        {/* Selectors Row */}
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '1rem', 
          marginBottom: '2rem',
          alignItems: 'flex-end',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          <div className="form-group" style={{ minWidth: '200px', flex: '1' }}>
            <label htmlFor="academic_year">Academic Year *</label>
            <select
              id="academic_year"
              value={selectedAcademicYear}
              onChange={(e) => setSelectedAcademicYear(e.target.value)}
              style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #D1D5DB' }}
            >
              <option value="">Select Academic Year</option>
              {academicYears.map(year => (
                <option key={year.id} value={year.id}>
                  {year.year_name || `${new Date(year.start_date).getFullYear()}-${new Date(year.end_date).getFullYear()}`}
                </option>
              ))}
            </select>
          </div>

          <BranchSelector
            schoolId={schoolData?.id}
            selectedBranchId={selectedBranchId}
            onBranchChange={handleBranchChange}
          />

          <div className="form-group" style={{ minWidth: '150px' }}>
            <label>View Mode</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => setViewMode('all')}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: viewMode === 'all' ? '#FF8C42' : '#E5E7EB',
                  color: viewMode === 'all' ? 'white' : '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '0.875rem'
                }}
              >
                All Classes
              </button>
              <button
                onClick={() => setViewMode('class-wise')}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: viewMode === 'class-wise' ? '#FF8C42' : '#E5E7EB',
                  color: viewMode === 'class-wise' ? 'white' : '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '0.875rem'
                }}
              >
                Class-wise
              </button>
            </div>
          </div>

          {viewMode === 'class-wise' && (
            <div className="form-group" style={{ minWidth: '200px', flex: '1' }}>
              <label htmlFor="class">Class</label>
              <select
                id="class"
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #D1D5DB' }}
              >
                <option value="">Select Class</option>
                {classes.map(cls => (
                  <option key={cls.id} value={cls.id}>
                    {getClassDisplayName(cls.class_name)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Invoices List */}
        <div className="dashboard-section" style={{ 
          width: '100%', 
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}>
          {loadingInvoices ? (
            <p style={{ textAlign: 'center', padding: '2rem' }}>Loading invoices...</p>
          ) : !selectedAcademicYear ? (
            <p style={{ textAlign: 'center', padding: '2rem', color: '#6B7280' }}>
              Please select an Academic Year to view invoices.
            </p>
          ) : invoices.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6B7280' }}>
              <p style={{ marginBottom: '1rem', fontSize: '1.125rem', fontWeight: '600' }}>
                No invoices found for the selected criteria.
              </p>
              <p style={{ marginBottom: '1.5rem' }}>
                To generate invoices, please go to the <strong>Billing</strong> page and generate invoices for your students.
              </p>
              <button
                onClick={() => navigate('/school/billing')}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'linear-gradient(135deg, #FF8C42 0%, #E67E22 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(255, 140, 66, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(255, 140, 66, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(255, 140, 66, 0.3)';
                }}
              >
                Go to Billing Page
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {invoices.map((invoice) => {
                const student = invoice.students;
                const invoiceItems = invoice.invoice_items || [];
                
                // Debug logging
                console.log('Invoice:', invoice);
                console.log('Invoice Items:', invoiceItems);
                console.log('Invoice Items Length:', invoiceItems.length);
                
                const studentName = `${student?.first_name || ''} ${student?.middle_name || ''} ${student?.last_name || ''}`.trim();
                const academicYear = academicYears.find(ay => ay.id === invoice.academic_year_id);

                return (
                  <div
                    key={invoice.id}
                    style={{
                      background: 'white',
                      borderRadius: '12px',
                      padding: '2rem',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      border: '1px solid #E5E7EB'
                    }}
                  >
                    {/* Invoice Header */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start',
                      marginBottom: '1.5rem',
                      paddingBottom: '1rem',
                      borderBottom: '2px solid #E5E7EB'
                    }}>
                      <div style={{ flex: 1 }}>
                        {schoolLogo && (
                          <img 
                            src={schoolLogo} 
                            alt="School Logo" 
                            style={{ maxHeight: '60px', marginBottom: '1rem' }}
                          />
                        )}
                        <h3 style={{ margin: '0 0 0.5rem 0', color: '#1F2937' }}>
                          {schoolData.school_name || 'School Name'}
                        </h3>
                        {schoolData.address && (
                          <p style={{ margin: '0.25rem 0', color: '#6B7280', fontSize: '0.875rem' }}>
                            {schoolData.address}
                          </p>
                        )}
                        {schoolData.phone && (
                          <p style={{ margin: '0.25rem 0', color: '#6B7280', fontSize: '0.875rem' }}>
                            Phone: {schoolData.phone}
                          </p>
                        )}
                        {schoolData.email && (
                          <p style={{ margin: '0.25rem 0', color: '#6B7280', fontSize: '0.875rem' }}>
                            Email: {schoolData.email}
                          </p>
                        )}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <h2 style={{ margin: '0 0 1rem 0', color: '#FF8C42' }}>INVOICE</h2>
                        <p style={{ margin: '0.25rem 0', color: '#6B7280', fontSize: '0.875rem' }}>
                          <strong>Invoice #:</strong> {invoice.invoice_number}
                        </p>
                        <p style={{ margin: '0.25rem 0', color: '#6B7280', fontSize: '0.875rem' }}>
                          <strong>Date:</strong> {new Date(invoice.generated_at).toLocaleDateString()}
                        </p>
                        {academicYear && (
                          <p style={{ margin: '0.25rem 0', color: '#6B7280', fontSize: '0.875rem' }}>
                            <strong>Academic Year:</strong> {academicYear.year_name || 'N/A'}
                          </p>
                        )}
                        {invoice.term && (
                          <p style={{ margin: '0.25rem 0', color: '#6B7280', fontSize: '0.875rem' }}>
                            <strong>Term:</strong> {invoice.term}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Student Details */}
                    <div style={{ marginBottom: '1.5rem' }}>
                      <h4 style={{ margin: '0 0 0.75rem 0', color: '#1F2937' }}>Bill To:</h4>
                      <p style={{ margin: '0.25rem 0', color: '#374151' }}>
                        <strong>Student Name:</strong> {studentName}
                      </p>
                      {student?.student_id_no && (
                        <p style={{ margin: '0.25rem 0', color: '#374151' }}>
                          <strong>Admission No:</strong> {student.student_id_no}
                        </p>
                      )}
                      {student?.classes?.class_name && (
                        <p style={{ margin: '0.25rem 0', color: '#374151' }}>
                          <strong>Class:</strong> {getClassDisplayName(student.classes.class_name)}
                        </p>
                      )}
                    </div>

                    {/* Fee Breakdown */}
                    <div style={{ marginBottom: '1.5rem' }}>
                      <h4 style={{ margin: '0 0 1rem 0', color: '#1F2937' }}>Fee Breakdown:</h4>
                      <table style={{ 
                        width: '100%', 
                        borderCollapse: 'collapse',
                        fontSize: '0.875rem'
                      }}>
                        <thead>
                          <tr style={{ background: '#F9FAFB' }}>
                            <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #E5E7EB' }}>
                              Fee Name
                            </th>
                            <th style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #E5E7EB' }}>
                              Amount
                            </th>
                            <th style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #E5E7EB' }}>
                              Discount
                            </th>
                            <th style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #E5E7EB' }}>
                              Balance
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {!invoiceItems || invoiceItems.length === 0 ? (
                            <tr>
                              <td colSpan="4" style={{ padding: '0.75rem', textAlign: 'center', color: '#6B7280', border: '1px solid #E5E7EB' }}>
                                <div>
                                  <p style={{ marginBottom: '0.5rem' }}>No fee items found for this invoice.</p>
                                  <p style={{ fontSize: '0.875rem', color: '#9CA3AF' }}>
                                    Invoice ID: {invoice.id} | Invoice Number: {invoice.invoice_number}
                                  </p>
                                  <p style={{ fontSize: '0.875rem', color: '#9CA3AF', marginTop: '0.5rem' }}>
                                    Please regenerate the invoice from the Billing page to include fee items.
                                  </p>
                                </div>
                              </td>
                            </tr>
                          ) : (
                            invoiceItems.map((item, index) => {
                              console.log(`Rendering invoice item ${index}:`, item);
                              return (
                                <tr key={item.id || index}>
                                  <td style={{ padding: '0.75rem', border: '1px solid #E5E7EB' }}>
                                    {item.fee_name || 'N/A'}
                                  </td>
                                  <td style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #E5E7EB' }}>
                                    Rs {formatNumber(item.amount)}
                                  </td>
                                  <td style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #E5E7EB' }}>
                                    Rs {formatNumber(item.discount)}
                                  </td>
                                  <td style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #E5E7EB' }}>
                                    Rs {formatNumber(item.balance)}
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Total Due */}
                    <div style={{ 
                      marginBottom: '1.5rem',
                      padding: '1rem',
                      background: '#F9FAFB',
                      borderRadius: '8px',
                      textAlign: 'right'
                    }}>
                      <p style={{ margin: '0.5rem 0', color: '#374151' }}>
                        <strong>Total Amount:</strong> Rs {formatNumber(invoice.total_amount)}
                      </p>
                      <p style={{ margin: '0.5rem 0', color: '#374151' }}>
                        <strong>Total Discount:</strong> Rs {formatNumber(invoice.total_discount)}
                      </p>
                      <p style={{ margin: '0.5rem 0', color: '#1F2937', fontSize: '1.125rem', fontWeight: '700' }}>
                        <strong>Total Due:</strong> Rs {formatNumber(invoice.total_balance)}
                      </p>
                    </div>

                    {/* Actions */}
                    <div style={{ 
                      display: 'flex', 
                      gap: '1rem', 
                      justifyContent: 'flex-end',
                      paddingTop: '1rem',
                      borderTop: '1px solid #E5E7EB'
                    }}>
                      <button
                        onClick={() => generateInvoicePDF(invoice)}
                        style={{
                          padding: '0.75rem 1.5rem',
                          background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                        }}
                      >
                        📥 Download PDF
                      </button>
                      <button
                        onClick={() => handlePrint(invoice)}
                        style={{
                          padding: '0.75rem 1.5rem',
                          background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                        }}
                      >
                        🖨️ Print
                      </button>
                      <button
                        onClick={() => handlePublishToParentApp(invoice)}
                        disabled={invoice.status === 'sent'}
                        style={{
                          padding: '0.75rem 1.5rem',
                          background: invoice.status === 'sent' 
                            ? '#D1D5DB' 
                            : 'linear-gradient(135deg, #FF8C42 0%, #E67E22 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          cursor: invoice.status === 'sent' ? 'not-allowed' : 'pointer',
                          transition: 'all 0.3s ease',
                          boxShadow: invoice.status === 'sent' 
                            ? 'none' 
                            : '0 4px 12px rgba(255, 140, 66, 0.3)',
                          opacity: invoice.status === 'sent' ? 0.6 : 1
                        }}
                        onMouseEnter={(e) => {
                          if (invoice.status !== 'sent') {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 6px 16px rgba(255, 140, 66, 0.4)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (invoice.status !== 'sent') {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 4px 12px rgba(255, 140, 66, 0.3)';
                          }
                        }}
                      >
                        {invoice.status === 'sent' ? '✅ Published' : '📱 Publish to Parent APP'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default InvoicePreview;
